import { useEffect, useState, useCallback, useMemo } from "react";
import clsx from "clsx";
import { paletteCommands } from "./navConfig";

const ALL_COMMANDS = paletteCommands();

export function CommandPalette({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const [q, setQ] = useState("");
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    if (!open) {
      setQ("");
      setHighlight(0);
    }
  }, [open]);

  const filtered = useMemo(
    () => ALL_COMMANDS.filter((c) => c.label.toLowerCase().includes(q.toLowerCase())),
    [q],
  );

  useEffect(() => {
    setHighlight(0);
  }, [q]);

  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(Math.max(0, filtered.length - 1));
  }, [filtered.length, highlight]);

  const selectHighlighted = useCallback(() => {
    const cmd = filtered[highlight];
    if (!cmd) return;
    onNavigate(cmd.path);
    onClose();
  }, [filtered, highlight, onNavigate, onClose]);

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        selectHighlighted();
      }
    },
    [open, onClose, filtered.length, selectHighlighted],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 pt-24" onClick={onClose}>
      <div
        className="panel w-full max-w-lg rounded-lg border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          className="w-full border-b border-border bg-bg px-4 py-3 text-sm outline-none"
          placeholder="Jump to module… (↑↓ Enter)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            }
            if (e.key === "Enter") {
              e.preventDefault();
              selectHighlighted();
            }
          }}
        />
        <ul className="max-h-80 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-4 py-2 text-sm text-muted">No matches</li>
          ) : (
            filtered.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={clsx(
                    "w-full px-4 py-2 text-left text-sm",
                    i === highlight ? "bg-accent/50 text-neutral-100" : "hover:bg-accent/40",
                  )}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => {
                    onNavigate(c.path);
                    onClose();
                  }}
                >
                  {c.label}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

export function useCommandPaletteToggle() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);
  return { open, setOpen };
}
